import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Video } from "lucide-react";
import { submitInterviewRequest } from "@/lib/interview-requests.functions";
import { requestInterviewGuide } from "@/lib/guide-signups.functions";
import headshot from "@/assets/chris-helvajian.jpg.asset.json";



const SITE_URL = "https://interview-reps.com";
const PAGE_TITLE = "REPS — Interview Coaching and Practice with Real Feedback";
const PAGE_DESCRIPTION =
  "Book a live virtual interview coaching session with REPS. Practice with role-specific questions and get real-time feedback to land your next role.";
const SOCIAL_TITLE = "REPS — Interview Coaching and Practice With Real Feedback";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      { property: "og:title", content: SOCIAL_TITLE },
      { property: "og:description", content: PAGE_DESCRIPTION },
      { property: "og:url", content: SITE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:title", content: SOCIAL_TITLE },
      { name: "twitter:description", content: PAGE_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],

    links: [{ rel: "canonical", href: SITE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "REPS Practice Interviews",
          serviceType: "Mock interview coaching",
          provider: { "@type": "Organization", name: "REPS", url: SITE_URL },
          areaServed: "Worldwide",
          description: PAGE_DESCRIPTION,
          offers: [
            {
              "@type": "Offer",
              name: "A Coaching Session",
              description: "One 20-minute general interview coaching session.",
              price: "49",
              priceCurrency: "USD",
              url: SITE_URL,
            },
            {
              "@type": "Offer",
              name: "The Sharpener",
              description: "One 30-minute live virtual mock interview with written feedback.",
              price: "99",
              priceCurrency: "USD",
              url: SITE_URL,
            },
            {
              "@type": "Offer",
              name: "The Deep Dive",
              description: "One 45-minute live virtual mock interview with written feedback.",
              price: "149",
              priceCurrency: "USD",
              url: SITE_URL,
            },
            {
              "@type": "Offer",
              name: "The Limitless",
              description: "Unlimited 30-minute practice interviews with feedback after every round.",
              price: "399",
              priceCurrency: "USD",
              url: SITE_URL,
            },
          ],
        }),
      },
    ],
  }),
});

type PackageId = "coaching-20" | "single-30" | "single-45" | "unlimited-30";

const PACKAGES: {
  id: PackageId;
  name: string;
  price: string;
  duration: string;
  tagline: string;
  perks: string[];
  featured?: boolean;
}[] = [
  {
    id: "coaching-20",
    name: "A Coaching Session",
    price: "$49",
    duration: "20 min",
    tagline: "General interview coaching, no JD required.",
    perks: ["20-minute virtual coaching call", "General interview strategy", "Q&A on your approach", "100% money-back if not satisfied"],
  },
  {
    id: "single-30",
    name: "The Sharpener",
    price: "$99",
    duration: "30 min",
    tagline: "One focused rep before your real interview.",
    perks: ["1 live virtual mock interview", "Custom questions from JD", "Detailed written feedback", "100% money-back if not satisfied"],
  },
  {
    id: "single-45",
    name: "The Deep Dive",
    price: "$149",
    duration: "45 min",
    tagline: "Perfect for more detailed interview prep.",
    perks: ["1 extended virtual mock interview", "Custom questions from JD", "Detailed written feedback", "100% money-back if not satisfied"],
    featured: true,
  },
  {
    id: "unlimited-30",
    name: "The Limitless",
    price: "$399",
    duration: "Unlimited practice interviews",
    tagline: "Rep until it's muscle memory.",
    perks: ["Unlimited practice interviews", "Fresh questions each session", "Feedback after every round", "100% money-back if not satisfied"],
  },
];

function Index() {
  const [selected, setSelected] = useState<PackageId>("single-45");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);



  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const selectedPackage = PACKAGES.find((p) => p.id === selected)!;

    try {
      await submitInterviewRequest({
        data: {
          packageId: selected,
          packageName: selectedPackage.name,
          packagePrice: selectedPackage.price,
          name: String(formData.get("name") ?? ""),
          email: String(formData.get("email") ?? ""),
          phone: String(formData.get("phone") ?? ""),
          role: String(formData.get("role") ?? ""),
          times: String(formData.get("times") ?? ""),
          jd: String(formData.get("jd") ?? ""),
        },
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-semibold focus:text-primary-foreground"
      >
        Skip to main content
      </a>
      {/* NAV */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-6">

        <a href="#top" className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-md gradient-ember font-display text-lg text-primary-foreground">
            R
          </span>
          <span className="font-display text-lg tracking-tight">REPS</span>
        </a>
        <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
          <a href="#packages" className="flex min-h-11 items-center hover:text-foreground">Packages</a>
          <a href="#how" className="flex min-h-11 items-center hover:text-foreground">How it works</a>
          <a href="#book" className="flex min-h-11 items-center hover:text-foreground">Book</a>
          <a href="#about" className="flex min-h-11 items-center hover:text-foreground">About</a>

        </nav>
        <a
          href="#book"
          className="flex min-h-11 min-w-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:brightness-110"
        >
          Book a session
        </a>
      </header>

      <main id="main">

      {/* HERO */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-full max-w-[820px] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
          style={{ background: "var(--gradient-ember)" }}
          aria-hidden
        />
        <div className="relative mx-auto min-w-0 max-w-7xl px-6 pb-24 pt-16 md:pt-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs uppercase tracking-widest text-muted-foreground backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Now booking interviews
          </div>
          <h1
            aria-label="REPS — Interview coaching + prep."
            className="font-display mt-6 text-balance break-words text-6xl md:text-8xl"
          >
            Interview <br />
            <span className="text-primary">coaching</span> + <br />
            <span className="text-accent">prep</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
            Live 1:1 virtual practice interviews for the role you actually applied to. Real
            questions from your job description, real-time feedback during our video call, and a written
            debrief in your inbox.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
            <Video className="h-3.5 w-3.5" />
            Virtual session via Zoom or Google Meet
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#book"
              className="flex min-h-12 min-w-12 items-center justify-center rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-ember transition hover:brightness-110"
            >
              Book a session →
            </a>
            <a
              href="#packages"
              className="flex min-h-12 min-w-12 items-center justify-center rounded-md border border-border bg-card px-6 py-3 text-base font-semibold text-foreground transition hover:bg-secondary"
            >
              See packages
            </a>
          </div>

          <dl className="mt-16 grid w-full grid-cols-3 gap-6 border-t border-border pt-8 text-center">
            {[
              { k: "20 – 45", v: "Minutes per session" },
              { k: "5 – 10", v: "Custom questions from your JD" },
              { k: "24h", v: "Written feedback turnaround" },
            ].map((s) => (
              <div key={s.v} className="min-w-0">
                <dt className="font-display text-4xl text-foreground md:text-5xl">{s.k}</dt>
                <dd className="mt-2 break-words text-xs uppercase tracking-widest text-muted-foreground">
                  {s.v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary">01 — Packages</p>
            <h2 className="font-display mt-3 text-5xl md:text-6xl">Pick your rep count.</h2>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
              Every package includes questions built from your job description,
              written feedback after each session, and a 100% money-back
              guarantee if you're not satisfied with the session.
            </p>
          </div>

          <div className="mt-14 grid items-stretch gap-6 md:grid-cols-3">
            {PACKAGES.map((p, i) => {
              const isSelected = selected === p.id;
              const isLast = i === PACKAGES.length - 1;
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={`Select ${p.name}, ${p.price}, ${p.duration}`}
                  onClick={() => {
                    setSelected(p.id);
                    document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={[
                    "group relative h-full rounded-xl border p-8 text-center transition",
                    isSelected
                      ? "border-primary bg-card shadow-ember"
                      : "border-border bg-card hover:border-muted-foreground/40",
                    isLast ? "md:col-span-3" : "flex flex-col",
                  ].join(" ")}
                >
                  {p.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full gradient-ember px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                      Most picked
                    </span>
                  )}
                  <div className={[
                    "flex h-full flex-col items-center justify-between gap-8",
                    isLast ? "md:flex-row md:items-center md:justify-evenly" : "",
                  ].join(" ")}>
                    <div className={isLast ? "md:max-w-sm" : "w-full"}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">
                        {p.duration}
                      </div>
                      <div className="font-display mt-4 text-3xl">{p.name}</div>
                      <div className="font-display mt-6 text-6xl text-primary">{p.price}</div>
                      <p className="mt-4 text-sm text-muted-foreground">{p.tagline}</p>
                    </div>
                    <div className={isLast ? "flex-1 md:max-w-md" : "w-full"}>
                      <ul className="mt-6 space-y-2 text-sm">
                        {p.perks.map((perk) => (
                          <li key={perk} className="flex justify-center gap-2">
                            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                            <span>{perk}</span>
                          </li>
                        ))}
                      </ul>
                      <div
                        className={[
                          "mt-8 inline-flex items-center gap-2 text-sm font-semibold",
                          isSelected ? "text-primary" : "text-foreground",
                        ].join(" ")}
                      >
                        {isSelected ? "Selected" : "Select →"}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <p className="text-xs uppercase tracking-widest text-primary">02 — How it works</p>
          <h2 className="font-display mt-3 text-5xl md:text-6xl">Four steps. No fluff.</h2>

          <ol className="mt-14 grid gap-8 md:grid-cols-4">
            {[
              {
                n: "01",
                t: "Sign up",
                d: "Pick a package and share your contact info + preferred times.",
              },
              {
                n: "02",
                t: "Send the JD",
                d: "Drop the job description so I can build role-specific questions.",
              },
              {
                n: "03",
                t: "Show up (virtually)",
                d: "I ask questions, you respond. We debrief near the end.",
              },
              {
                n: "04",
                t: "Get your debrief",
                d: "A hand-written feedback email lands in your inbox within 24 hours.",
              },
            ].map((s) => (
              <li key={s.n} className="border-t border-border pt-6">
                <div className="font-display text-4xl text-primary">{s.n}</div>
                <div className="mt-3 font-display text-2xl">{s.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="book" className="border-t border-border">
        <div className="mx-auto grid min-w-0 max-w-7xl gap-16 px-6 py-24 md:grid-cols-[1fr_1.3fr]">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-primary">03 — Book</p>
            <h2 className="font-display mt-3 text-balance break-words text-5xl md:text-6xl">
              Get on my calendar.
            </h2>
            <p className="mt-6 text-muted-foreground">
              Fill this out and I'll email you back within a day to lock in a
              virtual session over Zoom or Google Meet. Share the job
              description if you have it — otherwise just contact info is fine
              and we'll sort the rest over email. Note that I host a limited
              number of interviews per week, so don't delay in requesting your
              session.&nbsp;
            </p>
            <div className="mt-10 rounded-xl border border-border bg-card p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Currently selected
              </div>
              <div className="font-display mt-2 text-3xl">
                {PACKAGES.find((p) => p.id === selected)?.name}
              </div>
              <div className="mt-1 text-primary">
                {PACKAGES.find((p) => p.id === selected)?.price} ·{" "}
                {PACKAGES.find((p) => p.id === selected)?.duration}
              </div>
            </div>
          </div>

          {submitted ? (
            <div
              ref={successRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              className="flex min-w-0 flex-col justify-center rounded-xl border border-primary bg-card p-10 shadow-ember"
            >
              <div className="font-display text-4xl text-primary">You're in.</div>
              <p className="mt-4 text-muted-foreground">
                I got your request. Expect an email from me within 24 hours to confirm a time.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-8 flex min-h-11 items-center self-start text-sm font-semibold text-foreground underline underline-offset-4"
              >
                Submit another
              </button>
            </div>
          ) : (
            <div className="min-w-0 rounded-xl border border-border bg-card p-8 md:p-10">
              <form onSubmit={handleSubmit}>
                <div className="grid min-w-0 gap-5">
                  <div className="grid min-w-0 gap-2">
                    <label htmlFor="package" className="text-xs uppercase tracking-widest text-muted-foreground">
                      Package
                    </label>
                    <select
                      id="package"
                      value={selected}
                      onChange={(e) => setSelected(e.target.value as PackageId)}
                      className="w-full min-w-0 rounded-md border border-border bg-input px-4 py-3 font-medium text-foreground focus:border-primary"
                      required
                    >
                      {PACKAGES.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {p.price} ({p.duration})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid min-w-0 gap-5 sm:grid-cols-2">
                    <Field id="name" label="Your name" name="name" required placeholder="Alex Rivera" />
                    <Field
                      id="email"
                      label="Email"
                      name="email"
                      type="email"
                      required
                      placeholder="alex@example.com"
                    />
                  </div>
                  <div className="grid min-w-0 gap-5 sm:grid-cols-2">
                    <Field id="phone" label="Phone" name="phone" type="tel" placeholder="+1 555 000 1234" />
                    <Field
                      id="role"
                      label="THE ROLE YOU'RE TARGETING"
                      name="role"
                      placeholder="Senior PM at Acme"
                    />
                  </div>

                  <div className="grid min-w-0 gap-2">
                    <label htmlFor="times" className="text-xs uppercase tracking-widest text-muted-foreground">
                      PREFERRED TIMES AND DAYS (W/ TIME ZONE)
                    </label>
                    <input
                      id="times"
                      name="times"
                      placeholder="e.g. weekday evenings PST, or Tue/Thu mornings"
                      className="w-full rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
                    />
                  </div>

                  <div className="grid min-w-0 gap-2">
                    <label htmlFor="jd" className="text-xs uppercase tracking-widest text-muted-foreground">
                      Job description{" "}
                      <span className="text-muted-foreground">(optional but recommended)</span>
                    </label>
                    <textarea
                      id="jd"
                      name="jd"
                      rows={6}
                      maxLength={8000}
                      placeholder="Paste the JD here so I can tailor 5–10 questions to it…"
                      className="w-full resize-y rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
                    />
                    <p className="text-xs text-muted-foreground">8,000 character limit.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-2 flex min-h-12 w-full items-center justify-center rounded-md bg-primary px-6 py-4 font-semibold text-primary-foreground shadow-ember transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
                  >
                    {submitting ? "Sending…" : "Request my interview rep→"}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    Payment link comes in the confirmation email once we've locked a time.&nbsp;
                  </p>
                </div>
              </form>

              <div className="mt-5 flex justify-center">
                <GuideCapture />
              </div>
            </div>
          )}

        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-t border-border bg-secondary/30">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-6 py-24 md:grid-cols-[380px_1fr]">
          <div className="relative">
            <img
              src={headshot.url}
              alt="Chris Helvajian, senior recruiter and founder of REPS"
              loading="lazy"
              width={380}
              height={380}
              className="w-full rounded-xl border border-border object-cover shadow-ember"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-primary">04 — About me</p>
            <h2 className="font-display mt-3 text-5xl md:text-6xl">Hi, I'm Chris.</h2>
            <p className="mt-6 text-muted-foreground">
              I've spent 15+ years on the other side of the table as a recruiter and talent
              acquisition leader — at Included Health, BetterUp, UC Irvine, and Chapman University.
              In that time I've screened, interviewed, and evaluated thousands of candidates across
              GTM, product, design, finance, operations, and clinical roles. I know exactly what a
              hiring manager hears when you answer a question, what makes a recruiter lean in, and
              what quietly takes you out of the running.
            </p>
            <p className="mt-4 text-muted-foreground">
              I'm candidate-centric to the core. I've led teams in candidate satisfaction, rebuilt
              interview and communication processes around how it actually feels to be the person
              being interviewed. Coaching people to do well in interviews is the part I love most —
              helping you sharpen your story, tighten your examples, and walk into the room sounding
              like the most prepared person they'll talk to all week. That's what these reps are for.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      </main>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 p-4 shadow-[0_-8px_30px_rgba(0,0,0,0.25)] backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">Book your practice interview</p>
            <p className="truncate text-xs text-muted-foreground">From {PACKAGES[0].price} · Virtual session</p>
          </div>
          <a
            href="#book"
            className="flex shrink-0 min-h-12 min-w-12 items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-ember transition hover:brightness-110"
          >
            Book a session
          </a>
        </div>
      </div>

      <section
        aria-labelledby="preferred-source-heading"
        className="border-y border-border bg-card"
      >
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-6 py-12 text-center md:flex-row md:justify-between md:text-left">
          <div className="max-w-xl">
            <h2
              id="preferred-source-heading"
              className="font-display text-2xl text-foreground"
            >
              Follow REPS on Google
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Add interview-reps.com as a preferred source in Google News and Discover so you don't miss new interview tips and coaching updates.
            </p>
          </div>
          {mounted && (
            <div
              className="swg-button min-h-[40px]"
              data-theme="dark"
              data-lang="en"
              data-label="Add as Preferred Source"
            >
              <a
                href="https://www.google.com/preferences/source?q=interview-reps.com"
                className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
              >
                Add as Preferred Source
              </a>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-border pb-20 md:pb-0">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-10 md:flex-row md:items-center">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded gradient-ember font-display text-sm text-primary-foreground">
              R
            </span>
            <span className="font-display">REPS</span>
          </div>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Practice interviews that hit.
          </p>
        </div>
      </footer>
    </div>
  );
}

function GuideCapture() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const emailRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && state !== "done") emailRef.current?.focus();
  }, [open, state]);

  useEffect(() => {
    if (state === "done") doneRef.current?.focus();
  }, [state]);

  async function handleGuideSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState("sending");
    try {
      await requestInterviewGuide({ data: { email: email.trim() } });
      setState("done");
    } catch (err) {
      console.error("Guide signup failed:", err);
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <div
        ref={doneRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="rounded-xl border border-primary/40 bg-card p-6"
      >
        <div className="font-display text-2xl text-primary">Guide sent.</div>
        <p className="mt-2 text-sm text-muted-foreground">
          Check your inbox — the free interview guide is on its way to {email}.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        className="flex min-h-12 w-full items-center justify-center rounded-md bg-accent px-6 py-4 text-sm font-semibold text-accent-foreground transition hover:brightness-110 sm:w-auto"
      >
        Not quite ready? Grab a free interview guide!
      </button>
    );
  }


  return (
    <form
      onSubmit={handleGuideSubmit}
      className="rounded-xl border border-border bg-card p-6"
    >
      <div className="font-display text-2xl">Free interview guide</div>
      <p className="mt-2 text-sm text-muted-foreground">
        Drop your email and I'll send the general interview guide straight to your inbox.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="guide-email" className="sr-only">
          Email for the free interview guide
        </label>
        <input
          ref={emailRef}
          id="guide-email"
          name="guide-email"
          type="email"
          required
          maxLength={255}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="alex@example.com"
          className="flex-1 rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="flex min-h-12 items-center justify-center rounded-md bg-primary px-6 py-3 font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
        >
          {state === "sending" ? "Sending…" : "Send it →"}
        </button>
      </div>
      {state === "error" && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
}

function Field({
  id,
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  id: string;
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="grid min-w-0 gap-2">
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
      />
    </div>
  );
}
